import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createQuizAPI,
  getQuizAPI,
  getMyQuizzesAPI,
  getStudentQuizzesAPI,
  updateQuizAPI,
  deleteQuizAPI,
  assignQuizAPI,
  getQuizResultsAPI
} from "../../services/quiz";

export let createQuiz = createAsyncThunk(
  "quiz/create",
  async (quizData, thunkApi) => {
    try {
      let response = await createQuizAPI(quizData);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let getQuiz = createAsyncThunk(
  "quiz/get",
  async (quizId, thunkApi) => {
    try {
      let response = await getQuizAPI(quizId);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let getMyQuizzes = createAsyncThunk(
  "quiz/my",
  async (_, thunkApi) => {
    try {
      let response = await getMyQuizzesAPI();
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let getStudentQuizzes = createAsyncThunk(
  "quiz/student",
  async (_, thunkApi) => {
    try {
      let response = await getStudentQuizzesAPI();
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let updateQuiz = createAsyncThunk(
  "quiz/update",
  async ({ quizId, quizData }, thunkApi) => {
    try {
      let response = await updateQuizAPI(quizId, quizData);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let deleteQuiz = createAsyncThunk(
  "quiz/delete",
  async (quizId, thunkApi) => {
    try {
      let response = await deleteQuizAPI(quizId);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let assignQuiz = createAsyncThunk(
  "quiz/assign",
  async ({ quizId, studentId }, thunkApi) => {
    try {
      let response = await assignQuizAPI(quizId, studentId);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

export let getQuizResults = createAsyncThunk(
  "quiz/getResults",
  async (quizId, thunkApi) => {
    try {
      let response = await getQuizResultsAPI(quizId);
      return response.data;
    } catch (error) {
      let errorMsg = error.response?.data?.msg || error.response?.data?.message;
      return thunkApi.rejectWithValue(errorMsg ? errorMsg : error.message);
    }
  }
);

let initialState = {
  loading: false,
  quizzes: [],
  currentQuiz: null,
  quizResults: null,
  error: null,
  errorMsg: "",
  successMsg: ""
};

let quizSlice = createSlice({
  name: "quiz",
  initialState,
  reducers: {
    clearQuiz: (state, action) => {
      state.loading = false;
      state.error = null;
      state.errorMsg = "";
      state.successMsg = "";
      state.quizResults = null;
    },
    setCurrentQuiz: (state, action) => {
      state.currentQuiz = action.payload;
    }
  },
  extraReducers: {
    [createQuiz.pending]: (state, action) => {
      state.loading = true;
    },
    [createQuiz.fulfilled]: (state, action) => {
      state.loading = false;
      state.currentQuiz = action.payload.quiz;
      state.successMsg = "Quiz created successfully";
    },
    [createQuiz.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.errorMsg = "Failed to create quiz";
    },
    [getQuiz.pending]: (state, action) => {
      state.loading = true;
    },
    [getQuiz.fulfilled]: (state, action) => {
      state.loading = false;
      state.currentQuiz = action.payload.quiz;
    },
    [getQuiz.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    [getMyQuizzes.pending]: (state, action) => {
      state.loading = true;
    },
    [getMyQuizzes.fulfilled]: (state, action) => {
      state.loading = false;
      state.quizzes = action.payload.quizzes;
    },
    [getMyQuizzes.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    [getStudentQuizzes.pending]: (state, action) => {
      state.loading = true;
    },
    [getStudentQuizzes.fulfilled]: (state, action) => {
      state.loading = false;
      state.quizzes = action.payload.quizzes;
    },
    [getStudentQuizzes.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    [updateQuiz.pending]: (state, action) => {
      state.loading = true;
    },
    [updateQuiz.fulfilled]: (state, action) => {
      state.loading = false;
      state.currentQuiz = action.payload.quiz;
      state.successMsg = "Quiz updated successfully";
    },
    [updateQuiz.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.errorMsg = "Failed to update quiz";
    },
    [deleteQuiz.pending]: (state, action) => {
      state.loading = true;
    },
    [deleteQuiz.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMsg = "Quiz deleted successfully";
    },
    [deleteQuiz.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.errorMsg = "Failed to delete quiz";
    },
    [assignQuiz.pending]: (state, action) => {
      state.loading = true;
    },
    [assignQuiz.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMsg = "Quiz assigned successfully";
    },
    [assignQuiz.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.errorMsg = "Failed to assign quiz";
    },
    [getQuizResults.pending]: (state, action) => {
      state.loading = true;
    },
    [getQuizResults.fulfilled]: (state, action) => {
      state.loading = false;
      state.quizResults = action.payload;
    },
    [getQuizResults.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.errorMsg = "Failed to fetch quiz results";
    }
  }
});

let quizReducer = quizSlice.reducer;
export let { clearQuiz, setCurrentQuiz } = quizSlice.actions;
export default quizReducer;

